"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

function median(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function upperHalf(sorted: number[]): number[] {
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return sorted.slice(mid);
  }
  return sorted.slice(mid + 1);
}

function computeModes(values: number[]): number[] {
  const freq = new Map<number, number>();
  for (const value of values) {
    freq.set(value, (freq.get(value) ?? 0) + 1);
  }
  let max = 0;
  for (const count of freq.values()) {
    if (count > max) {
      max = count;
    }
  }
  if (max <= 1) {
    return [];
  }
  const modes: number[] = [];
  for (const [value, count] of freq) {
    if (count === max) {
      modes.push(value);
    }
  }
  return modes.sort((a, b) => a - b);
}

interface Stats {
  count: number;
  sum: number;
  min: number;
  max: number;
  range: number;
  mean: number;
  median: number;
  modes: number[];
  sampleVariance: number | null;
  sampleStdDev: number | null;
  populationVariance: number;
  populationStdDev: number;
  q1: number | null;
  q3: number | null;
  iqr: number | null;
}

function computeVariances(
  values: number[],
  mean: number
): { sample: number | null; population: number } {
  let sumSquares = 0;
  for (const value of values) {
    sumSquares += (value - mean) ** 2;
  }
  const population = sumSquares / values.length;
  const sample = values.length >= 2 ? sumSquares / (values.length - 1) : null;
  return { sample, population };
}

function computeQuartiles(sorted: number[]): {
  q1: number | null;
  q3: number | null;
  iqr: number | null;
} {
  if (sorted.length < 2) {
    return { q1: null, q3: null, iqr: null };
  }
  const q1 = median(sorted.slice(0, Math.floor(sorted.length / 2)));
  const q3 = median(upperHalf(sorted));
  return { q1, q3, iqr: q3 - q1 };
}

function computeStats(values: number[]): Stats | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((acc, v) => acc + v, 0);
  const mean = sum / values.length;
  const min = sorted[0];
  const max = sorted.at(-1) ?? min;
  const variances = computeVariances(values, mean);
  const quartiles = computeQuartiles(sorted);
  return {
    count: values.length,
    sum,
    min,
    max,
    range: max - min,
    mean,
    median: median(sorted),
    modes: computeModes(values),
    sampleVariance: variances.sample,
    sampleStdDev:
      variances.sample === null ? null : Math.sqrt(variances.sample),
    populationVariance: variances.population,
    populationStdDev: Math.sqrt(variances.population),
    q1: quartiles.q1,
    q3: quartiles.q3,
    iqr: quartiles.iqr,
  };
}

/**
 * The machine-readable form. ASCII digits, ASCII period.
 *
 * This is what `Copy Results` puts on the clipboard and what the mode list joins
 * with commas, and both leave the page: a copied `1,5` pasted into a spreadsheet is
 * a different number, and `1,5, 2,5` is not a list of two numbers in any language.
 * `formatStatDisplay` is the one the reader sees.
 */
function formatStat(value: number | null, dash: string): string {
  if (value === null) {
    return dash;
  }
  return Number(value.toFixed(4)).toString();
}

/** The same statistic as the reader's own language writes it. */
function formatStatDisplay(
  value: number | null,
  dash: string,
  fmt: NumberFormatter
): string {
  if (value === null) {
    return dash;
  }
  return fmt(value, { maximumFractionDigits: 4 });
}

function formatModes(modes: number[], dash: string, none: string): string {
  if (modes.length === 0) {
    return none;
  }
  return modes.map((m) => formatStat(m, dash)).join(", ");
}

/** English strings. A translated content file overrides these through `ui`. */
export const STATISTICS_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  placeholder: "12, 15, 9, 22\n18 14 15\n20",
  numbersDetected: "one:{count} number detected|other:{count} numbers detected",
  copied: "Copied!",
  copyResults: "Copy Results",
  clear: "Clear",
  results: "Results",
  none: "None",
  dash: "—",
  count: "Count",
  sum: "Sum",
  minimum: "Minimum",
  maximum: "Maximum",
  range: "Range",
  mean: "Mean",
  median: "Median",
  modes: "Mode(s)",
  sampleVariance: "Sample variance",
  sampleStdDeviation: "Sample std. deviation",
  populationVariance: "Population variance",
  populationStdDeviation: "Population std. deviation",
  q1: "Q1 (first quartile)",
  q3: "Q3 (third quartile)",
  iqr: "IQR",
  resultCount: "Count: ",
  resultSum: "Sum: ",
  resultMinimum: "Minimum: ",
  resultMaximum: "Maximum: ",
  resultRange: "Range: ",
  resultMean: "Mean: ",
  resultMedian: "Median: ",
  resultModes: "Mode(s): ",
  resultSampleVariance: "Sample variance: ",
  resultSampleStdDeviation: "Sample std. deviation: ",
  resultPopulationVariance: "Population variance: ",
  resultPopulationStdDeviation: "Population std. deviation: ",
  resultQ1: "Q1: ",
  resultQ3: "Q3: ",
  resultIqr: "IQR: ",
} as const;

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function StatisticsCalculatorWidget() {
  const strings = useToolUi(STATISTICS_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);

  const values = parseNumberList(input);
  const stats = computeStats(values);

  function buildResultsText(s: Stats): string {
    const lines = [
      `${strings.resultCount}${s.count}`,
      `${strings.resultSum}${formatStat(s.sum, strings.dash)}`,
      `${strings.resultMinimum}${formatStat(s.min, strings.dash)}`,
      `${strings.resultMaximum}${formatStat(s.max, strings.dash)}`,
      `${strings.resultRange}${formatStat(s.range, strings.dash)}`,
      `${strings.resultMean}${formatStat(s.mean, strings.dash)}`,
      `${strings.resultMedian}${formatStat(s.median, strings.dash)}`,
      `${strings.resultModes}${formatModes(s.modes, strings.dash, strings.none)}`,
      `${strings.resultSampleVariance}${formatStat(s.sampleVariance, strings.dash)}`,
      `${strings.resultSampleStdDeviation}${formatStat(s.sampleStdDev, strings.dash)}`,
      `${strings.resultPopulationVariance}${formatStat(s.populationVariance, strings.dash)}`,
      `${strings.resultPopulationStdDeviation}${formatStat(s.populationStdDev, strings.dash)}`,
      `${strings.resultQ1}${formatStat(s.q1, strings.dash)}`,
      `${strings.resultQ3}${formatStat(s.q3, strings.dash)}`,
      `${strings.resultIqr}${formatStat(s.iqr, strings.dash)}`,
    ];
    return lines.join("\n");
  }

  function handleCopy() {
    if (!stats) {
      return;
    }
    writeToClipboard(buildResultsText(stats)).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="stats-input"
        >
          {strings.dataLabel}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="stats-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.placeholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.numbersDetected, locale, values.length, {
            count: fmt(values.length),
          })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!stats}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResults}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Results */}
      {stats && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.results}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard label={strings.count} value={fmt(stats.count)} />
            <StatCard
              label={strings.sum}
              value={formatStatDisplay(stats.sum, strings.dash, fmt)}
            />
            <StatCard
              label={strings.minimum}
              value={formatStatDisplay(stats.min, strings.dash, fmt)}
            />
            <StatCard
              label={strings.maximum}
              value={formatStatDisplay(stats.max, strings.dash, fmt)}
            />
            <StatCard
              label={strings.range}
              value={formatStatDisplay(stats.range, strings.dash, fmt)}
            />
            <StatCard
              label={strings.mean}
              value={formatStatDisplay(stats.mean, strings.dash, fmt)}
            />
            <StatCard
              label={strings.median}
              value={formatStatDisplay(stats.median, strings.dash, fmt)}
            />
            <StatCard
              label={strings.modes}
              value={formatModes(stats.modes, strings.dash, strings.none)}
            />
            <StatCard
              label={strings.sampleVariance}
              value={formatStatDisplay(stats.sampleVariance, strings.dash, fmt)}
            />
            <StatCard
              label={strings.sampleStdDeviation}
              value={formatStatDisplay(stats.sampleStdDev, strings.dash, fmt)}
            />
            <StatCard
              label={strings.populationVariance}
              value={formatStatDisplay(
                stats.populationVariance,
                strings.dash,
                fmt
              )}
            />
            <StatCard
              label={strings.populationStdDeviation}
              value={formatStatDisplay(
                stats.populationStdDev,
                strings.dash,
                fmt
              )}
            />
            <StatCard
              label={strings.q1}
              value={formatStatDisplay(stats.q1, strings.dash, fmt)}
            />
            <StatCard
              label={strings.q3}
              value={formatStatDisplay(stats.q3, strings.dash, fmt)}
            />
            <StatCard
              label={strings.iqr}
              value={formatStatDisplay(stats.iqr, strings.dash, fmt)}
            />
          </div>
        </section>
      )}
    </>
  );
}
